import { Controller } from "@hotwired/stimulus";

// Controls the dynamic transaction form behavior
export default class extends Controller {
  static targets = [
    "studentSearch",
    "studentInfo",
    "feeTypeSelect",
    "amountField",
    "originalAmountField",
    "discountsList",
    "addDiscountButton",
    "totalAmount",
    "discountTemplate",
    "installmentSelection",
    "feePlanSelection",
    "feePlanPeriodSelection",
    "feeTariffSelection",
    "extracurricularActivitySelection",
    "expectedPaymentsSection",
    "expectedPaymentsList",
    "expectedPaymentRadio",
    "expectedPaymentIdField",
    "partialPaymentOptions",
    "partialAmountInput",
    "partialAmountField",
    "partialAmountHint",
    "fullAmountDisplay",
    "isPartialPaymentField",
    "partialAmountHiddenField",
    "toggleManualButton",
    "manualFeeSelection",
    "manualToggle",
  ];

  // Fee type constants
  static values = {
    feeTypes: {
      transport: "fees.TRANSPORT",
      cafeteria: "fees.CAFETERIA",
      boarding: "fees.BOARDING",
      childcare: "fees.CHILDCARE",
      documents: "fees.DOCUMENTS",
      health: "fees.HEALTH",
      uniform: "fees.UNIFORM",
      supplies: "fees.SUPPLIES",
      extracurricular: "fees.EXTRACURRICULAR_ACTIVITIES",
      fieldTrip: "fees.FIELD_TRIP",
      tuition: "fees.TUITION",
      registration: "fees.REGISTRATION",
      exam: "fees.EXAM",
    },
    // Endpoints configurables
    studentSearchUrl: String,
    studentInfoUrl: String,
    expectedPaymentsUrl: String,
    feeTypesUrl: String,
    applicableDiscountsUrl: String,
    feeSettingAmountInfoUrl: String,
    feeSettingTypeInfoUrl: String,
    feePlansUrl: String,
    feeTariffsUrl: String,
    extracurricularActivitiesUrl: String,
    installmentsUrl: String,
    feePlanAmountUrl: String,
    feeTariffAmountUrl: String,
    extracurricularActivityAmountUrl: String,
    installmentAmountUrl: String,
  };

  connect() {
    console.log("Transaction form controller connected");
  }

  // Student search functionality
  searchStudent(event) {
    const query = event.target.value;
    if (query.length < 3) return;

    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      const url = `${this.studentSearchUrlValue}?query=${encodeURIComponent(
        query,
      )}`;

      fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      })
        .then((response) => response.text())
        .then((html) => {
          Turbo.renderStreamMessage(html);
        });
    }, 300);
  }

  selectStudent(event) {
    event.preventDefault();

    const studentId = event.currentTarget.dataset.studentId;
    const url = `${this.studentInfoUrlValue}?student_id=${studentId}`;

    // Load student info
    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);

        // Montrer la section des paiements attendus
        if (this.hasExpectedPaymentsSectionTarget) {
          this.expectedPaymentsSectionTarget.classList.remove("hidden");
        }

        // Charger les paiements attendus pour cet étudiant
        this.loadExpectedPayments(studentId);

        // Et les frais eux-memes : le selecteur restait vide tant qu'on
        // n'avait pas rechargé la page, alors que le point d'entrée
        // existait deja et n'etait appele de nulle part.
        this.loadFeeTypes(studentId);
      });
  }

  loadFeeTypes(studentId) {
    // La categorie n'est pas toujours a l'ecran — et sans elle le point
    // d'entree leve cote serveur. On s'abstient plutot que d'echouer.
    const categoryId = document.querySelector(
      "#financial_transaction_financial_transaction_category_id",
    )?.value;
    if (!categoryId) return;

    const url = `${this.feeTypesUrlValue}?student_id=${studentId}&category_id=${categoryId}`;

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
      });
  }

  // When fee type is selected, update the amount field
  feeTypeChanged(event) {
    const feeSettingId = event.target.value;
    if (!feeSettingId) return;

    // Hide all specific fee type sections
    this.hideAllFeeTypeSelections();

    const url = this.feeSettingAmountInfoUrlValue.replace(
      "__ID__",
      feeSettingId,
    );

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
        this.loadApplicableDiscounts();

        // After loading fee setting info, show the appropriate form sections
        this.showRelevantFeeOptions(feeSettingId);
      });
  }

  // Show the relevant fee options based on fee type
  showRelevantFeeOptions(feeSettingId) {
    if (!feeSettingId) return;

    // Fetch the fee setting type
    const url = this.feeSettingTypeInfoUrlValue.replace("__ID__", feeSettingId);

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // Hide all sections first
        this.hideAllFeeTypeSelections();

        // Show the appropriate section based on fee type
        const feeTypeName = data.fee_type_name;

        console.log({ feeTypeName });
        switch (feeTypeName) {
          // Transport, cantine, internat et garderie se facturent tous par
          // formules periodiques : meme selecteur, meme point d'entree.
          case this.constructor.values.feeTypes.transport:
          case this.constructor.values.feeTypes.cafeteria:
          case this.constructor.values.feeTypes.boarding:
          case this.constructor.values.feeTypes.childcare:
            this.showPlanOptions(feeSettingId);
            break;
          // Documents, sante, uniformes et fournitures se facturent a l'acte,
          // sur une grille tarifaire : une prestation, un prix unitaire.
          case this.constructor.values.feeTypes.documents:
          case this.constructor.values.feeTypes.health:
          case this.constructor.values.feeTypes.uniform:
          case this.constructor.values.feeTypes.supplies:
            this.showTariffOptions(feeSettingId);
            break;
          // Une sortie scolaire se choisit comme une activité.
          case this.constructor.values.feeTypes.extracurricular:
          case this.constructor.values.feeTypes.fieldTrip:
            this.showExtracurricularOptions(feeSettingId);
            break;
          default:
            this.showInstallmentOptions(feeSettingId);
            break;
        }
      });
  }

  // Hide all fee type-specific sections
  hideAllFeeTypeSelections() {
    if (this.hasInstallmentSelectionTarget) {
      this.installmentSelectionTarget.classList.add("hidden");
    }

    if (this.hasFeePlanSelectionTarget) {
      this.feePlanSelectionTarget.classList.add("hidden");
    }

    if (this.hasFeePlanPeriodSelectionTarget) {
      this.feePlanPeriodSelectionTarget.classList.add("hidden");
    }

    if (this.hasFeeTariffSelectionTarget) {
      this.feeTariffSelectionTarget.classList.add("hidden");
    }

    if (this.hasExtracurricularActivitySelectionTarget) {
      this.extracurricularActivitySelectionTarget.classList.add("hidden");
    }
  }

  // Show fee plan options
  //
  // Le flux turbo remplace les deux blocs — la formule et sa periode — parce
  // que les periodes proposees dependent du type de frais. Les rendus de
  // remplacement arrivent visibles : rien a demasquer derriere.
  showPlanOptions(feeSettingId) {
    if (!this.hasFeePlanSelectionTarget) return;

    this.feePlanSelectionTarget.classList.remove("hidden");

    const url = this.feePlansUrlValue.replace("__ID__", feeSettingId);

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
      });
  }

  // Show fee tariff options
  showTariffOptions(feeSettingId) {
    if (!this.hasFeeTariffSelectionTarget) return;

    this.feeTariffSelectionTarget.classList.remove("hidden");

    const url = this.feeTariffsUrlValue.replace("__ID__", feeSettingId);

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
      });
  }

  // When the fee tariff is changed
  feeTariffChanged(event) {
    const tariffId = event.target.value;
    if (!tariffId) return;

    const url = this.feeTariffAmountUrlValue.replace("__ID__", tariffId);

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.amount) {
          this.originalAmountFieldTarget.value = data.amount;
          this.updateTotalAmount();
        }
      });
  }

  // Show extracurricular activity options
  showExtracurricularOptions(feeSettingId) {
    if (this.hasExtracurricularActivitySelectionTarget) {
      this.extracurricularActivitySelectionTarget.classList.remove("hidden");

      // Load extracurricular activities
      const url = this.extracurricularActivitiesUrlValue.replace(
        "__ID__",
        feeSettingId,
      );

      fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      })
        .then((response) => response.text())
        .then((html) => {
          Turbo.renderStreamMessage(html);
        });
    }
  }

  // Show installment options (for tuition, registration, exam, etc.)
  showInstallmentOptions(feeSettingId) {
    if (this.hasInstallmentSelectionTarget) {
      this.installmentSelectionTarget.classList.remove("hidden");

      // Load installments
      const url = this.installmentsUrlValue.replace("__ID__", feeSettingId);

      fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      })
        .then((response) => response.text())
        .then((html) => {
          Turbo.renderStreamMessage(html);
        });
    }
  }

  // When the fee plan is changed
  feePlanChanged(event) {
    const planId = event.target.value;
    if (!planId) return;

    // Get billing period
    const billingPeriod = document.querySelector(
      "#financial_transaction_fee_plan_billing_period",
    ).value;
    if (!billingPeriod) return;

    // Fetch the fee plan rate for the billing period
    const url = `${this.feePlanAmountUrlValue.replace(
      "__ID__",
      planId,
    )}?billing_period=${billingPeriod}`;

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.amount) {
          this.originalAmountFieldTarget.value = data.amount;
          this.updateTotalAmount();
        }
      });
  }

  // When the fee plan billing period is changed
  feePlanPeriodChanged(event) {
    const billingPeriod = event.target.value;
    if (!billingPeriod) return;

    const planId = document.querySelector(
      "#financial_transaction_fee_plan_id",
    ).value;
    if (!planId) return;

    // Fetch the fee plan rate for the billing period
    const url = `${this.feePlanAmountUrlValue.replace(
      "__ID__",
      planId,
    )}?billing_period=${billingPeriod}`;

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.amount) {
          this.originalAmountFieldTarget.value = data.amount;
          this.updateTotalAmount();
        }
      });
  }

  // When extracurricular activity is changed
  extracurricularActivityChanged(event) {
    const activityId = event.target.value;
    if (!activityId) return;

    // Fetch the activity amount
    const url = this.extracurricularActivityAmountUrlValue.replace(
      "__ID__",
      activityId,
    );

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.amount) {
          this.originalAmountFieldTarget.value = data.amount;
          this.updateTotalAmount();
        }
      });
  }

  // When installment is changed
  installmentChanged(event) {
    const installmentId = event.target.value;
    if (!installmentId) return;

    // Fetch the installment amount
    const url = this.installmentAmountUrlValue.replace("__ID__", installmentId);

    fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.amount) {
          this.originalAmountFieldTarget.value = data.amount;
          this.updateTotalAmount();
        }
      });
  }

  // Load applicable discounts for this student and fee type
  // Le panneau des remises est rendu par le serveur : lui seul connait la
  // remise deja portee par le du, son montant et le lien vers elle.
  loadApplicableDiscounts(expectedPaymentId = null) {
    const studentId = document.querySelector(
      "#financial_transaction_student_id",
    )?.value;
    const feeSettingId = document.querySelector(
      "#financial_transaction_fee_setting_id",
    )?.value;

    if (!studentId || !feeSettingId) return;

    let url = `${this.applicableDiscountsUrlValue}?student_id=${studentId}&fee_setting_id=${feeSettingId}`;
    if (expectedPaymentId) {
      url += `&student_expected_payment_id=${expectedPaymentId}`;
    }

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);
        // Le panneau remplace l'affichage du total : sans ce recalcul, un
        // montant deja saisi (une prestation au tarif, par exemple) serait
        // remis a zero a l'ecran.
        this.updateTotalAmount();
      });
  }

  // Discount management

  // Apply an existing discount from the available discounts list
  applyExistingDiscount(event) {
    event.preventDefault();

    const discountId = event.currentTarget.dataset.discountId;
    const discountType = event.currentTarget.dataset.discountType;
    const discountValue = event.currentTarget.dataset.discountValue;

    // Get the original amount
    const originalAmount =
      parseFloat(this.originalAmountFieldTarget.value) || 0;
    if (originalAmount <= 0) {
      alert("Veuillez d'abord saisir un montant original valide.");
      return;
    }

    // Calculate the discount amount
    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (
        (originalAmount * parseFloat(discountValue)) /
        100
      ).toFixed(2);
    } else {
      discountAmount = parseFloat(discountValue).toFixed(2);
    }

    // Create a new discount field
    this.addDiscountField(event, {
      discountId,
      amount: discountAmount,
      amountSaved: Math.min(discountAmount, originalAmount),
    });
  }

  // Add a new empty discount field
  addDiscountField(event, data = {}) {
    event.preventDefault();

    // Get the discount template and clone it
    if (!this.hasDiscountTemplateTarget) return;

    const clone = this.discountTemplateTarget.innerHTML;

    // Update the clone with a unique index
    const timestamp = new Date().getTime();
    const newHtml = clone.replace(/NEW_RECORD/g, timestamp);

    // Create a temporary container
    const temp = document.createElement("div");
    temp.innerHTML = newHtml;

    // If we have prefill data, set it
    if (data.discountId) {
      const selectElement = temp.querySelector("select");
      if (selectElement) {
        selectElement.value = data.discountId;
      }

      const amountInput = temp.querySelector(".discount-amount");
      if (amountInput && data.amount) {
        amountInput.value = data.amount;
      }

      const amountSavedInput = temp.querySelector(".discount-amount-saved");
      if (amountSavedInput && data.amountSaved) {
        amountSavedInput.value = data.amountSaved;
      }

      const discountInputId = temp.querySelector(
        "#financial_transaction_transaction_discounts_attributes_NEW_RECORD_fee_discount_id".replace(
          "NEW_RECORD",
          timestamp,
        ),
      );
      if (discountInputId && data.discountId) {
        discountInputId.value = data.discountId;
      }
    }

    // Add the new field to the list
    const appliedDiscounts = document.getElementById("applied_discounts");
    if (appliedDiscounts) {
      // Remove the "no discounts" message if present
      const noDiscountsMessage = appliedDiscounts.querySelector(
        "p.text-gray-500.text-sm.italic",
      );
      if (noDiscountsMessage) {
        noDiscountsMessage.remove();
      }

      appliedDiscounts.appendChild(temp);
    }

    // Update the total amount
    this.updateTotalAmount();
  }

  // When a discount is changed
  discountChanged(event) {
    const select = event.target;
    const discountId = select.value;
    if (!discountId) return;

    // Find the fee discount in the available discounts
    const discountButton = document.querySelector(
      `[data-discount-id="${discountId}"]`,
    );
    if (!discountButton) return;

    const discountType = discountButton.dataset.discountType;
    const discountValue = discountButton.dataset.discountValue;

    // Get the original amount
    const originalAmount =
      parseFloat(this.originalAmountFieldTarget.value) || 0;
    if (originalAmount <= 0) return;

    // Calculate the discount amount
    let discountAmount = 0;
    if (discountType === "percentage") {
      discountAmount = (
        (originalAmount * parseFloat(discountValue)) /
        100
      ).toFixed(2);
    } else {
      discountAmount = parseFloat(discountValue).toFixed(2);
    }

    // Update the discount amount field
    const discountField = select.closest(".discount-field");
    const amountInput = discountField.querySelector(".discount-amount");
    if (amountInput) {
      amountInput.value = discountAmount;
    }

    // Update the amount saved field
    const amountSavedInput = discountField.querySelector(
      ".discount-amount-saved",
    );
    if (amountSavedInput) {
      amountSavedInput.value = Math.min(discountAmount, originalAmount);
    }

    // Update the total amount
    this.updateTotalAmount();
  }

  // Update the amount saved when a custom discount amount is entered
  updateDiscountSaved(event) {
    const input = event.target;
    const discountAmount = parseFloat(input.value) || 0;

    // Get the original amount
    const originalAmount =
      parseFloat(this.originalAmountFieldTarget.value) || 0;
    if (originalAmount <= 0) return;

    // Update the amount saved field (cannot exceed original amount)
    const discountField = input.closest(".discount-field");
    const amountSavedInput = discountField.querySelector(
      ".discount-amount-saved",
    );
    if (amountSavedInput) {
      amountSavedInput.value = Math.min(discountAmount, originalAmount);
    }

    // Update the total amount
    this.updateTotalAmount();
  }

  // Remove a discount field
  removeDiscount(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const discountField = button.closest(".discount-field");

    // Vérifier si cet élément a un ID (s'il est déjà en base de données)
    const idField = discountField.querySelector("input[name$='[id]']");
    console.log({ idField });
    if (idField && idField.value) {
      // C'est un discount existant : on le masque et on définit _destroy = 1
      const destroyInput = discountField.querySelector(
        "input[name$='[_destroy]']",
      );
      if (destroyInput) {
        destroyInput.value = "1";
        discountField.style.display = "none";
      }
    } else {
      // C'est un nouveau discount : on le supprime complètement du DOM
      discountField.remove();
    }

    // Check if there are any visible discounts left
    const appliedDiscounts = document.getElementById("applied_discounts");
    if (
      appliedDiscounts &&
      !appliedDiscounts.querySelectorAll(
        ".discount-field:not([style*='display: none'])",
      ).length
    ) {
      // Add "no discounts" message back
      appliedDiscounts.innerHTML += `<p class="text-gray-500 text-sm italic">Aucune remise n'a été appliquée</p>`;
    }

    // Update the total amount
    this.updateTotalAmount();
  }

  // Apply a discount
  applyDiscount(event) {
    this.addDiscountField(event);
  }

  // Update the total amount based on the original amount and applied discounts
  updateTotalAmount() {
    // Get the original amount
    const originalAmount =
      parseFloat(this.originalAmountFieldTarget.value) || 0;

    // For each discount, recalculate the amount saved
    document
      .querySelectorAll(".discount-field:not([style*='display: none'])")
      .forEach((discountField) => {
        const discountType =
          discountField.querySelector("select")?.dataset?.discountType ||
          document.querySelector(
            `[data-discount-id="${
              discountField.querySelector("select")?.value
            }"]`,
          )?.dataset?.discountType;
        const discountValue =
          discountField.querySelector("select")?.dataset?.discountValue ||
          document.querySelector(
            `[data-discount-id="${
              discountField.querySelector("select")?.value
            }"]`,
          )?.dataset?.discountValue;

        if (discountType && discountValue) {
          // Calculate the discount amount
          let discountAmount = 0;
          if (discountType === "percentage") {
            discountAmount = (
              (originalAmount * parseFloat(discountValue)) /
              100
            ).toFixed(2);
          } else {
            discountAmount = parseFloat(discountValue).toFixed(2);
          }

          // Update the amount field
          const amountInput = discountField.querySelector(".discount-amount");
          if (amountInput) {
            amountInput.value = discountAmount;
          }

          // Update the amount saved field
          const amountSavedInput = discountField.querySelector(
            ".discount-amount-saved",
          );
          if (amountSavedInput) {
            amountSavedInput.value = Math.min(discountAmount, originalAmount);
          }
        }
      });

    // Get the sum of all discount amounts saved (en ignorant ceux qui sont masqués/supprimés)
    let totalDiscounts = 0;
    document
      .querySelectorAll(
        ".discount-field:not([style*='display: none']) .discount-amount-saved",
      )
      .forEach((input) => {
        totalDiscounts += parseFloat(input.value) || 0;
      });

    // Calculate the final amount
    const finalAmount = Math.max(0, originalAmount - totalDiscounts);

    // Format the amount as currency
    const formatter = new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    // Update the display and hidden field
    this.totalAmountTarget.textContent =
      formatter.format(finalAmount) + " F CFA";
    this.amountFieldTarget.value = finalAmount;
    if (document.getElementById("financial_transaction_amount")) {
      document.getElementById("financial_transaction_amount").value =
        finalAmount;
    }
  }

  // When the payment method changes, show/hide the reference number field
  paymentMethodChanged(event) {
    const select = event.target;
    const requiresReference =
      select.options[select.selectedIndex].dataset.requiresReference;
    const referenceField = document.getElementById("reference_number_field");
    if (referenceField) {
      if (requiresReference === "true") {
        referenceField.classList.remove("hidden");
      } else {
        referenceField.classList.add("hidden");
      }
    }
  }

  // When the entity type changes for fee category transactions
  entityTypeChanged(event) {
    const select = event.target;
    const entityType = select.value;
    // Do any necessary updates based on entity type
  }

  // === Nouvelles méthodes pour gérer les paiements attendus ===

  // Mise à jour de la méthode selectStudent pour charger les paiements attendus
  selectStudentWithExpectedPayments(event) {
    event.preventDefault();

    const studentId = event.currentTarget.dataset.studentId;
    const url = `${this.studentInfoUrlValue}?student_id=${studentId}`;

    // Load student info
    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);

        // Montrer la section des paiements attendus
        if (this.hasExpectedPaymentsSectionTarget) {
          this.expectedPaymentsSectionTarget.classList.remove("hidden");
        }

        // Charger les paiements attendus pour cet étudiant
        this.loadExpectedPayments(studentId);
      });
  }

  // Charger les paiements attendus pour un étudiant
  loadExpectedPayments(studentId) {
    const categoryId = document.querySelector(
      "#financial_transaction_financial_transaction_category_id",
    )?.value;

    if (!categoryId) return;

    const url = `${this.expectedPaymentsUrlValue}?student_id=${studentId}&category_id=${categoryId}`;

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);

        // La saisie manuelle ne s'efface que s'il y a bien des dus a
        // proposer : un eleve sans du n'avait plus aucun moyen de payer,
        // les deux panneaux etant masques en meme temps.
        const hasDues =
          document.querySelectorAll(".expected-payment-item").length > 0;
        if (this.hasManualFeeSelectionTarget) {
          this.manualFeeSelectionTarget.classList.toggle("hidden", hasDues);
        }
        if (this.hasManualToggleTarget) {
          this.manualToggleTarget.classList.toggle("hidden", !hasDues);
          this.manualToggleTarget.dataset.mode = "expected";
          this.manualToggleTarget.textContent =
            this.manualToggleTarget.dataset.labelManual;
        }
      });
  }

  // Sélectionner un paiement attendu
  selectExpectedPayment(event) {
    event.preventDefault();

    const paymentItem = event.currentTarget;
    const paymentId = paymentItem.dataset.expectedPaymentId;
    const amount = parseFloat(paymentItem.dataset.amount);
    const allowsPartial = paymentItem.dataset.allowsPartial === "true";
    const minimumPartial = parseFloat(paymentItem.dataset.minimumPartial) || 0;

    // Mettre à jour le radio button
    const radio = paymentItem.querySelector("input[type='radio']");
    if (radio) {
      radio.checked = true;
    }

    // C'est la carte qui montre le choix, pas le radio : il est hors de vue.
    document
      .querySelectorAll(".expected-payment-item")
      .forEach((card) => card.classList.remove("is-selected"));
    paymentItem.classList.add("is-selected");

    // Stocker l'ID dans le champ caché
    if (this.hasExpectedPaymentIdFieldTarget) {
      this.expectedPaymentIdFieldTarget.value = paymentId;
    }

    // Le du porte son frais : sans le reporter, le panneau des remises ne
    // savait pas de quel parametrage parler et restait muet.
    const feeSettingField = document.querySelector(
      "#financial_transaction_fee_setting_id",
    );
    if (feeSettingField && paymentItem.dataset.feeSettingId) {
      feeSettingField.value = paymentItem.dataset.feeSettingId;
    }

    // Mettre à jour le montant original
    if (this.hasOriginalAmountFieldTarget) {
      this.originalAmountFieldTarget.value = amount;
    }

    // Afficher les options de paiement partiel si autorisé
    if (allowsPartial && this.hasPartialPaymentOptionsTarget) {
      this.partialPaymentOptionsTarget.classList.remove("hidden");

      if (this.hasFullAmountDisplayTarget) {
        this.fullAmountDisplayTarget.textContent = `(${this.formatCurrency(
          amount,
        )})`;
      }

      // Stocker les valeurs pour validation
      this.selectedPaymentAmount = amount;
      this.minimumPartialAmount = minimumPartial;
    } else {
      if (this.hasPartialPaymentOptionsTarget) {
        this.partialPaymentOptionsTarget.classList.add("hidden");
      }
    }

    // Recalculer le total
    this.updateTotalAmount();

    // Puis rafraichir les remises pour CE du.
    this.loadApplicableDiscounts(paymentId);
  }

  // Changer le type de paiement (complet ou partiel)
  paymentTypeChanged(event) {
    const paymentType = event.target.value;

    if (paymentType === "partial" && this.hasPartialAmountInputTarget) {
      this.partialAmountInputTarget.classList.remove("hidden");

      if (this.hasIsPartialPaymentFieldTarget) {
        this.isPartialPaymentFieldTarget.value = "true";
      }
    } else {
      if (this.hasPartialAmountInputTarget) {
        this.partialAmountInputTarget.classList.add("hidden");
      }

      if (this.hasIsPartialPaymentFieldTarget) {
        this.isPartialPaymentFieldTarget.value = "false";
      }

      // Réinitialiser au montant complet
      if (this.hasOriginalAmountFieldTarget && this.selectedPaymentAmount) {
        this.originalAmountFieldTarget.value = this.selectedPaymentAmount;
        this.updateTotalAmount();
      }
    }
  }

  // Valider le montant partiel
  validatePartialAmount(event) {
    const partialAmount = parseFloat(event.target.value) || 0;
    const hint = this.hasPartialAmountHintTarget
      ? this.partialAmountHintTarget
      : null;

    if (!hint) return;

    if (partialAmount <= 0) {
      hint.textContent = "Le montant doit être supérieur à 0";
      hint.className = "text-sm mt-1 text-red-600";
      this.clearPartialAmount();
      return;
    }

    if (
      this.minimumPartialAmount &&
      partialAmount < this.minimumPartialAmount
    ) {
      hint.textContent = `Le montant minimum est de ${this.formatCurrency(
        this.minimumPartialAmount,
      )}`;
      hint.className = "text-sm mt-1 text-red-600";
      this.clearPartialAmount();
      return;
    }

    if (
      this.selectedPaymentAmount &&
      partialAmount > this.selectedPaymentAmount
    ) {
      hint.textContent = `Le montant ne peut pas dépasser ${this.formatCurrency(
        this.selectedPaymentAmount,
      )}`;
      hint.className = "text-sm mt-1 text-red-600";
      this.clearPartialAmount();
      return;
    }

    // Validation réussie
    hint.textContent = `Montant valide (${this.formatCurrency(
      this.selectedPaymentAmount - partialAmount,
    )} restera dû)`;
    hint.className = "text-sm mt-1 text-cta";

    // Mettre à jour le montant dans le formulaire
    if (this.hasOriginalAmountFieldTarget) {
      this.originalAmountFieldTarget.value = partialAmount;
    }

    if (this.hasPartialAmountHiddenFieldTarget) {
      this.partialAmountHiddenFieldTarget.value = partialAmount;
    }

    this.updateTotalAmount();
  }

  // An amount the hint refuses must not leave the last valid keystroke in
  // the hidden field: 70 000 typed over a 50 000 due was recorded as 7 000.
  // Emptied, the server refuses the partial payment instead. The amount
  // fields the last valid keystroke filled are emptied too (QA R2-02): the
  // next steps showed it as the amount kept.
  clearPartialAmount() {
    const fields = [
      this.hasPartialAmountHiddenFieldTarget && this.partialAmountHiddenFieldTarget,
      this.hasOriginalAmountFieldTarget && this.originalAmountFieldTarget,
      this.hasAmountFieldTarget && this.amountFieldTarget,
    ];
    fields.filter(Boolean).forEach((field) => {
      field.value = "";
    });
  }

  // Basculer vers la saisie manuelle
  toggleManualEntry(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const isManual = button.dataset.mode !== "manual";

    if (isManual) {
      // Passer en mode manuel
      button.dataset.mode = "manual";
      // Les libelles viennent du gabarit : ecrits ici, ils echappaient a la
      // traduction et l'ecran anglais affichait du francais.
      button.textContent = button.dataset.labelExpected;

      // Masquer la section des paiements attendus
      if (this.hasExpectedPaymentsSectionTarget) {
        this.expectedPaymentsSectionTarget.classList.add("hidden");
      }

      // Afficher la sélection manuelle
      if (this.hasManualFeeSelectionTarget) {
        this.manualFeeSelectionTarget.classList.remove("hidden");
      }

      // Réinitialiser les champs
      this.resetExpectedPaymentFields();
    } else {
      // Revenir aux paiements attendus
      button.dataset.mode = "expected";
      button.textContent = button.dataset.labelManual;

      // Afficher la section des paiements attendus
      if (this.hasExpectedPaymentsSectionTarget) {
        this.expectedPaymentsSectionTarget.classList.remove("hidden");
      }

      // Masquer la sélection manuelle
      if (this.hasManualFeeSelectionTarget) {
        this.manualFeeSelectionTarget.classList.add("hidden");
      }
    }
  }

  // Réinitialiser les champs des paiements attendus
  resetExpectedPaymentFields() {
    if (this.hasExpectedPaymentIdFieldTarget) {
      this.expectedPaymentIdFieldTarget.value = "";
    }

    if (this.hasIsPartialPaymentFieldTarget) {
      this.isPartialPaymentFieldTarget.value = "false";
    }

    if (this.hasPartialAmountHiddenFieldTarget) {
      this.partialAmountHiddenFieldTarget.value = "";
    }

    if (this.hasPartialPaymentOptionsTarget) {
      this.partialPaymentOptionsTarget.classList.add("hidden");
    }

    // Décocher tous les radio buttons
    document
      .querySelectorAll('input[name="selected_expected_payment"]')
      .forEach((radio) => {
        radio.checked = false;
      });

    this.selectedPaymentAmount = null;
    this.minimumPartialAmount = null;
  }

  // Utilitaire pour formater les montants
  formatCurrency(amount) {
    const formatter = new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(amount) + " F CFA";
  }
}
